import type { ScalarGrid } from '../types';

/**
 * What a cell with a missing corner gives: `'propagate'` gives null, so nothing is drawn where the
 * interpolation has nothing to stand on; `'nearest'` gives the corner nearest the point, null only
 * when that corner is missing too.
 */
export type MissingCorner = 'propagate' | 'nearest';

/**
 * A field value at fractional lattice coordinates, blended bilinearly from the four samples around
 * it. Coordinates are clamped to the lattice, so a point a rounding error past its edge reads the
 * edge rather than extrapolating past the data.
 */
export const sampleLattice = (
  grid: ScalarGrid,
  gridX: number,
  gridY: number,
  missing: MissingCorner
): number | null => {
  if (grid.width < 2 || grid.height < 2) return null;
  const x = Math.min(grid.width - 1, Math.max(0, gridX));
  const y = Math.min(grid.height - 1, Math.max(0, gridY));
  const column = Math.min(grid.width - 2, Math.floor(x));
  const row = Math.min(grid.height - 2, Math.floor(y));
  const tx = x - column;
  const ty = y - row;
  const at = (dx: number, dy: number) =>
    grid.values[(row + dy) * grid.width + column + dx];
  const corners = [at(0, 0), at(1, 0), at(0, 1), at(1, 1)];

  if (corners.every(value => value !== null)) {
    const [lowLeft, lowRight, highLeft, highRight] = corners as number[];
    const low = lowLeft + (lowRight - lowLeft) * tx;
    const high = highLeft + (highRight - highLeft) * tx;
    return low + (high - low) * ty;
  }
  return missing === 'nearest' ? at(Math.round(tx), Math.round(ty)) : null;
};
